import React, {FormEvent, useEffect, useMemo, useRef, useState} from 'react'
import {useForm} from 'react-hook-form'
import {useHistory, useLocation} from 'react-router-dom'
import {
  checkDraftExistsByMobile,
  deleteRenterProfile,
  loadApplication,
  loadHousehold,
  loadPropertySubmissions,
  confirmFoundPlace,
  loadReviewSummary,
  revokePropertySubmission,
  restorePropertySubmission,
  saveApplication,
  sendWizardOtp,
  verifyWizardOtp,
  getCoApplicantPrimaryApplicant,
} from '../../api'
import {
  IApplicationFormData,
  IWizardReviewSummary,
  normalizeApplicationFormData,
} from '../../models/application-form'
import {MobileNumberInput} from '../../../common/component/MobileNumberInput'
import {OTPInput} from '../../../common/component/OTPInput'
import ApplicationsTab from './components/ApplicationsTab'
import FoundPlaceModal, {FoundPlaceModalView} from './components/FoundPlaceModal'
import HouseholdTab from './components/HouseholdTab'
import ProfileTab from './components/ProfileTab'
import ApplicationResponsiveModal from '../common/ApplicationResponsiveModal'
import {useRetryCountdown} from '../common/useRetryCountdown'
import {
  AccessStep,
  AU_MOBILE_REGEX,
  formatDate,
  formatAustralianMobile,
  getApplicantFirstName,
  getApplicantName,
  getSessionIdFromSearch,
  hasCompletedDooraCard,
  normalizeMobile,
  RENTER_HOME_PATH,
  RenterHomeTab,
  ScaffoldSubmission,
  type SubmitToPropertyPrefill,
} from './renterHomeUtils'
import '../../style/renter-home.scss'
import {RenterHomeHero} from './components/RenterHomeHero'
import {
  getRetentionPreferenceText,
  RetentionPreferenceValue,
} from '../../models/retention-preference'
import {FoundPlaceConfirmationRequest} from '../../models/found-place'
import {Bounce, ToastContainer} from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

const HOUSEHOLD_COMPOSITION_DISMISSAL_STORAGE_KEY = 'doora.householdCompositionPrompt.dismissedKeys'

const readDismissedHouseholdCompositionKeys = (): string[] => {
  if (typeof window === 'undefined') {
    return []
  }

  try {
    const raw = window.localStorage.getItem(HOUSEHOLD_COMPOSITION_DISMISSAL_STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed)
      ? parsed.filter((item): item is string => typeof item === 'string')
      : []
  } catch {
    return []
  }
}

const writeDismissedHouseholdCompositionKeys = (keys: string[]) => {
  if (typeof window === 'undefined') {
    return
  }

  window.localStorage.setItem(HOUSEHOLD_COMPOSITION_DISMISSAL_STORAGE_KEY, JSON.stringify(keys))
}

const isActiveSubmission = (submission: ScaffoldSubmission): boolean =>
  submission.status !== 'revoked' && submission.status !== 'expired'

const buildHouseholdCompositionPromptKey = (
  sessionId: string,
  submissions: ScaffoldSubmission[]
): string =>
  [
    sessionId,
    ...submissions
      .map((submission) => [
        submission.id,
        submission.householdCompositionChangeType ?? 'none',
        submission.currentHouseholdMemberCount ?? 0,
        submission.submittedHouseholdMemberCount ?? 0,
        ...(submission.addedHouseholdMemberNames ?? []),
        ...(submission.removedHouseholdMemberNames ?? []),
      ])
      .flat(),
  ].join('|')

const buildSubmitToPropertyPrefill = (submission: ScaffoldSubmission): SubmitToPropertyPrefill => {
  const agents =
    submission.agents && submission.agents.length > 0
      ? submission.agents.map((agent) => ({
          agentName: agent.name,
          agentPhone: agent.phone,
          agentEmail: agent.email,
        }))
      : [
          {
            agentName: submission.agentName,
            agentPhone: submission.agentPhone,
            agentEmail: submission.agentEmail,
          },
        ]

  return {
    propertyAddress: submission.address,
    propertyPlaceId: submission.placeId,
    agentName: agents[0]?.agentName,
    agentPhone: agents[0]?.agentPhone,
    agentEmail: agents[0]?.agentEmail,
    agents,
    resubmittedFromSubmissionId: submission.id,
  }
}

const RenterHomePage: React.FC = () => {
  const history = useHistory()
  const location = useLocation()
  const sessionId = useMemo(() => getSessionIdFromSearch(location.search), [location.search])
  const tabFromSearch = new URLSearchParams(location.search).get('tab')

  const [activeTab, setActiveTab] = useState<RenterHomeTab>(
    tabFromSearch === 'household' || tabFromSearch === 'profile' ? tabFromSearch : 'applications'
  )
  const [accessStep, setAccessStep] = useState<AccessStep>('mobile')
  const [mobileNumber, setMobileNumber] = useState('')
  const [otpCode, setOtpCode] = useState('')
  const sendRetry = useRetryCountdown()
  const verifyRetry = useRetryCountdown()
  const [accessMessage, setAccessMessage] = useState<string | null>(null)
  const [homeMessage, setHomeMessage] = useState<string | null>(null)
  const [isSendingCode, setIsSendingCode] = useState(false)
  const [isVerifyingCode, setIsVerifyingCode] = useState(false)
  const [isLoadingHome, setIsLoadingHome] = useState(Boolean(sessionId))
  const [homeReloadKey, setHomeReloadKey] = useState(0)
  const [draft, setDraft] = useState<IApplicationFormData | null>(null)
  const [summary, setSummary] = useState<IWizardReviewSummary | null>(null)
  const [household, setHousehold] = useState<IWizardReviewSummary | null>(null)
  const [submissions, setSubmissions] = useState<ScaffoldSubmission[]>([])
  const [submissionRefreshFailed, setSubmissionRefreshFailed] = useState(false)
  const [isRefreshingSubmissions, setIsRefreshingSubmissions] = useState(false)
  const submissionRefreshVersion = useRef(0)
  const [foundPlaceModalView, setFoundPlaceModalView] = useState<FoundPlaceModalView | null>(null)
  const [revokingSubmissionId, setRevokingSubmissionId] = useState<string | null>(null)
  const [restoringSubmissionId, setRestoringSubmissionId] = useState<string | null>(null)
  const [isSavingRetentionPreference, setIsSavingRetentionPreference] = useState(false)
  const [dismissedHouseholdCompositionKeys, setDismissedHouseholdCompositionKeys] = useState<
    string[]
  >(readDismissedHouseholdCompositionKeys)
  const [primaryApplicant, setPrimaryApplicant] = useState<IApplicationFormData | null>(null)
  const [isPrimaryApplicant, setIsPrimaryApplicant] = useState(false)

  const applicantName = getApplicantName(summary, draft)
  const applicantFirstName = getApplicantFirstName(summary, draft)
  const isCompletedCard = useMemo(() => hasCompletedDooraCard(summary), [summary])
  const submittedCount = submissions.length
  const viewedCount = submissions.filter((s) => s.status === 'viewed').length
  const affectedHouseholdSubmissions = useMemo(
    () =>
      isPrimaryApplicant
        ? submissions.filter(
            (submission) =>
              isActiveSubmission(submission) && submission.isHouseholdCompositionOutdated
          )
        : [],
    [isPrimaryApplicant, submissions]
  )
  const householdCompositionPromptKey = useMemo(
    () =>
      sessionId && affectedHouseholdSubmissions.length > 0
        ? buildHouseholdCompositionPromptKey(sessionId, affectedHouseholdSubmissions)
        : null,
    [affectedHouseholdSubmissions, sessionId]
  )
  const shouldShowHouseholdCompositionPrompt = Boolean(
    householdCompositionPromptKey &&
      !dismissedHouseholdCompositionKeys.includes(householdCompositionPromptKey)
  )

  useEffect(() => {
    if (!sessionId) {
      setDraft(null)
      setSummary(null)
      setHousehold(null)
      setSubmissions([])
      setIsLoadingHome(false)
      return
    }

    let cancelled = false
    setIsLoadingHome(true)
    setAccessMessage(null)

    const loadHome = async () => {
      try {
        const [loadedDraft, loadedSummary, loadedSubmissions, loadedHousehold] = await Promise.all([
          loadApplication(sessionId),
          loadReviewSummary(sessionId),
          loadPropertySubmissions(sessionId),
          loadHousehold(sessionId).catch(() => null),
        ])

        if (cancelled) {
          return
        }

        const renterRole = loadedDraft?.renterRole?.trim().toLowerCase() ?? ''
        var isPrimaryApplicant = renterRole === '' || renterRole === 'primary'

        setDraft(normalizeApplicationFormData(loadedDraft))
        setSummary(loadedSummary)
        setHousehold(loadedHousehold ?? loadedSummary)
        setSubmissions(loadedSubmissions as ScaffoldSubmission[])
        setIsPrimaryApplicant(isPrimaryApplicant)


        if (!isPrimaryApplicant) {
          var primaryApplicant = await getCoApplicantPrimaryApplicant(sessionId)
          setPrimaryApplicant(normalizeApplicationFormData(primaryApplicant))
        }
      } catch {
        if (!cancelled) {
          setDraft(null)
          setSummary(null)
          setHousehold(null)
          setSubmissions([])
          setAccessMessage(
            'Your secure access has expired. Verify your mobile to open your Doora Card.'
          )
        }
      } finally {
        if (!cancelled) {
          setIsLoadingHome(false)
        }
      }
    }

    loadHome()

    return () => {
      cancelled = true
    }
  }, [sessionId, homeReloadKey])

  const handleSendCode = async (mobileNumberInput: string) => {
    if (isSendingCode || sendRetry.seconds > 0 || verifyRetry.seconds > 0) {
      return
    }

    const normalizedMobile = normalizeMobile(mobileNumberInput)
    setAccessMessage(null)
    setOtpCode('')

    if (!AU_MOBILE_REGEX.test(normalizedMobile)) {
      setAccessMessage('Enter a valid Australian mobile number.')
      return
    }

    setIsSendingCode(true)

    try {
      const existingDraft = await checkDraftExistsByMobile(normalizedMobile)
      if (!existingDraft.exists) {
        setAccessMessage('No Doora Card was found for that mobile number. Build your card first.')
        return
      }

      const result = await sendWizardOtp({mobileNumber: normalizedMobile})
      sendRetry.start(result.retryAfterSeconds)
      if (!result.sent) {
        setAccessMessage(result.message ?? 'Unable to send a code right now.')
        return
      }

      setMobileNumber(normalizedMobile)
      setAccessStep('otp')
      setAccessMessage('Code sent. Enter the 6-digit code to continue.')
    } catch {
      setAccessMessage('Unable to send a code right now.')
    } finally {
      setIsSendingCode(false)
    }
  }

  const handleVerifyCode = async (event: FormEvent) => {
    event.preventDefault()
    if (isVerifyingCode || verifyRetry.seconds > 0) return

    const normalizedMobile = normalizeMobile(mobileNumber)
    const normalizedCode = otpCode.trim()
    setAccessMessage(null)

    if (!AU_MOBILE_REGEX.test(normalizedMobile)) {
      setAccessStep('mobile')
      setAccessMessage('Enter your mobile number again.')
      return
    }

    if (normalizedCode.length !== 6) {
      setAccessMessage('Enter the 6-digit code.')
      return
    }

    setIsVerifyingCode(true)

    try {
      const result = await verifyWizardOtp({
        mobileNumber: normalizedMobile,
        otpCode: normalizedCode,
        action: 'resume',
      })

      if (!result.verified || !result.sessionId) {
        verifyRetry.start(result.retryAfterSeconds)
        if (result.isLockedOut) sendRetry.start(result.retryAfterSeconds)
        if (result.requiresNewCode) setAccessStep('mobile')
        setAccessMessage(result.failureCode ?? 'Unable to verify that code.')
        return
      }

      const verifiedSessionId = result.sessionId.trim()
      const currentSessionId = sessionId?.trim()

      setAccessMessage(null)
      setOtpCode('')

      if (currentSessionId && currentSessionId.toLowerCase() === verifiedSessionId.toLowerCase()) {
        setHomeReloadKey((current) => current + 1)
        return
      }

      history.replace(`${RENTER_HOME_PATH}?sessionId=${encodeURIComponent(verifiedSessionId)}`)
    } catch {
      setAccessMessage('Unable to verify that code right now.')
    } finally {
      setIsVerifyingCode(false)
    }
  }

  const goToWizard = (step = 'review') => {
    if (sessionId) {
      history.push(`/application-wizard/${step}?sessionId=${encodeURIComponent(sessionId)}`)
      return
    }

    history.push('/application-wizard')
  }

  const editProfileSection = (step: string, scrollTarget?: string) => {
    if (!sessionId || !draft) {
      return
    }

    history.push({
      pathname: `${RENTER_HOME_PATH}/edit/${encodeURIComponent(step)}`,
      search: `?sessionId=${encodeURIComponent(sessionId)}`,
      state: {
        initialApplicationData: {
          ...draft,
          reviewSummary: summary,
        },
        wizardScrollTarget: scrollTarget,
      },
    })
  }

  const showScaffoldNotice = (feature: string) => {
    console.log('feature', feature)
    setHomeMessage(
      `${feature} is not available yet. This workflow is covered by the next renter dashboard tickets.`
    )
  }

  const updateRetentionPreference = async (value: RetentionPreferenceValue) => {
    if (!draft || !sessionId || isSavingRetentionPreference) {
      return
    }

    const previousDraft = draft
    const updatedDraft: IApplicationFormData = {
      ...draft,
      sessionId,
      retentionPreference: value,
      retentionPreferenceText: getRetentionPreferenceText(value),
    }

    setDraft(updatedDraft)
    setHomeMessage(null)
    setIsSavingRetentionPreference(true)

    try {
      const result = await saveApplication(updatedDraft, {
        isAutosave: false,
        stepKey: 'privacy',
        updateRetentionPreference: true,
      })

      setDraft((current) =>
        current
          ? {
              ...current,
              applicationId: result.applicationId,
              status: result.status === 'submitted' ? 'submitted' : 'draft',
              lastSavedAtUtc: result.savedAtUtc,
            }
          : current
      )
      setHomeMessage('Data retention preference updated.')
    } catch {
      setDraft(previousDraft)
      setHomeMessage('Unable to update data retention right now.')
    } finally {
      setIsSavingRetentionPreference(false)
    }
  }

  const openSubmitPage = (prefillSubmission?: ScaffoldSubmission) => {
    if (!sessionId || !draft) {
      return
    }

    if (household?.householdEligibility?.canSubmit === false) {
      const blockerMessages = household.householdEligibility.blockingReasons
        .map((blocker) => blocker.message)
        .filter(Boolean)
      setHomeMessage(
        blockerMessages[0] ?? 'Your household needs attention before it can be submitted.'
      )
      setActiveTab('household')
      return
    }

    const submissionPrefill = prefillSubmission
      ? buildSubmitToPropertyPrefill(prefillSubmission)
      : undefined

    history.push({
      pathname: `${RENTER_HOME_PATH}/submit`,
      search: `?sessionId=${encodeURIComponent(sessionId)}`,
      state: {
        initialApplicationData: {
          ...draft,
          reviewSummary: household ?? summary,
        },
        submissionPrefill,
      },
    })
  }

  const dismissHouseholdCompositionPrompt = () => {
    if (!householdCompositionPromptKey) {
      return
    }

    setDismissedHouseholdCompositionKeys((current) => {
      if (current.includes(householdCompositionPromptKey)) {
        return current
      }

      const next = [...current, householdCompositionPromptKey]
      writeDismissedHouseholdCompositionKeys(next)
      return next
    })
  }

  const refreshHousehold = async () => {
    if (!sessionId) {
      return
    }

    try {
      const [updatedDraft, updatedSummary, updatedHousehold, updatedSubmissions] =
        await Promise.all([
          loadApplication(sessionId),
          loadReviewSummary(sessionId),
          loadHousehold(sessionId).catch(() => null),
          loadPropertySubmissions(sessionId).catch(() => null),
        ])
      setDraft(normalizeApplicationFormData(updatedDraft))
      setSummary(updatedSummary)
      setHousehold(updatedHousehold ?? updatedSummary)
      if (updatedSubmissions) {
        setSubmissions(updatedSubmissions as ScaffoldSubmission[])
      }
    } catch {
      setHomeMessage('The household was updated, but the list could not be refreshed.')
    }
  }

  const refreshSubmissions = async () => {
    if (!sessionId) return
    const refreshVersion = ++submissionRefreshVersion.current
    setIsRefreshingSubmissions(true)
    try {
      const updated = await loadPropertySubmissions(sessionId)
      if (refreshVersion !== submissionRefreshVersion.current) return
      setSubmissions(updated as ScaffoldSubmission[])
      setSubmissionRefreshFailed(false)
      return updated as ScaffoldSubmission[]
    } catch {
      if (refreshVersion === submissionRefreshVersion.current) setSubmissionRefreshFailed(true)
    } finally {
      if (refreshVersion === submissionRefreshVersion.current) setIsRefreshingSubmissions(false)
    }
  }

  const handleRestoreSubmissionAccess = async (submissionId: string) => {
    if (!sessionId || restoringSubmissionId || revokingSubmissionId) {
      return
    }

    const targetSubmission = submissions.find((submission) => submission.id === submissionId)
    if (!targetSubmission || targetSubmission.status !== 'revoked') {
      return
    }

    if (targetSubmission.expiresInDays <= 0) {
      setHomeMessage('Access has expired and cannot be restored.')
      return
    }

    setRestoringSubmissionId(submissionId)
    setHomeMessage(null)

    try {
      const updated = await restorePropertySubmission({sessionId, submissionId})
      setSubmissions((current) =>
        current.map((submission) =>
          submission.id === submissionId ? (updated as ScaffoldSubmission) : submission
        )
      )
      setHomeMessage('Access restored. Existing property manager links can be used again.')
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message.replace(/^Failed to restore submission:\s*/i, '')
          : ''
      setHomeMessage(message || 'Unable to restore access right now. Please try again.')
    } finally {
      setRestoringSubmissionId(null)
    }
  }

  const revokeSubmissionAndUpdate = async (submissionId: string): Promise<boolean> => {
    if (!sessionId || revokingSubmissionId || restoringSubmissionId) {
      return false
    }

    const previousSubmissions = submissions
    const revokedAt = new Date().toISOString()
    setRevokingSubmissionId(submissionId)
    setHomeMessage(null)
    setSubmissions((current) =>
      current.map((submission) =>
        submission.id === submissionId
          ? {
              ...submission,
              status: 'revoked',
              revokedAt,
              expiresInDays: 0,
            }
          : submission
      )
    )

    try {
      const updated = await revokePropertySubmission({sessionId, submissionId})
      setSubmissions((current) =>
        current.map((submission) =>
          submission.id === submissionId ? (updated as ScaffoldSubmission) : submission
        )
      )
      return true
    } catch {
      setSubmissions(previousSubmissions)
      setHomeMessage('Unable to revoke access right now. Please try again.')
      return false
    } finally {
      setRevokingSubmissionId(null)
    }
  }

  const handleRevokeSubmission = async (submissionId: string) => {
    await revokeSubmissionAndUpdate(submissionId)
  }

  const handleRevokeAllSubmissions = async () => {
    if (!sessionId) {
      throw new Error('Your secure session is unavailable. Please sign in again.')
    }
    if (revokingSubmissionId || restoringSubmissionId) {
      throw new Error('Wait for your current submission update to finish, then try again.')
    }

    ++submissionRefreshVersion.current
    setIsRefreshingSubmissions(true)
    setHomeMessage(null)
    try {
      let latest: ScaffoldSubmission[]
      try {
        latest = (await loadPropertySubmissions(sessionId)) as ScaffoldSubmission[]
      } catch {
        setSubmissionRefreshFailed(true)
        throw new Error('Unable to load current submissions. No revocations were started. Please try again.')
      }

      setSubmissions(latest)
      setSubmissionRefreshFailed(false)
      const isEligible = (submission: ScaffoldSubmission) =>
        isActiveSubmission(submission) &&
        !submission.revokedAt &&
        (!submission.expiresAt || new Date(submission.expiresAt).getTime() > Date.now())
      const active = latest.filter(isEligible)
      let failedCount = 0
      for (const submission of active) {
        try {
          const updated = await revokePropertySubmission({sessionId, submissionId: submission.id})
          setSubmissions((current) =>
            current.map((item) => item.id === submission.id ? (updated as ScaffoldSubmission) : item)
          )
          if (isActiveSubmission(updated as ScaffoldSubmission)) failedCount++
        } catch {
          failedCount++
        }
      }

      const refreshed = await refreshSubmissions()
      const remainingCount = refreshed ? refreshed.filter(isEligible).length : failedCount
      if (remainingCount > 0) {
        throw new Error(
          `Revocation could not be confirmed for ${remainingCount} active submission(s). ` +
          'Successfully revoked submissions remain revoked. Your DOORA Card has been kept. ' +
          'Choose Keep DOORA Card, Revoke All Submissions again to retry the remaining submissions.'
        )
      }

      setHomeMessage('All active property submissions have been revoked. Your DOORA Card remains active.')
    } finally {
      setIsRefreshingSubmissions(false)
    }
  }

  const handleDeleteRenterProfile = async () => {
    if (!sessionId) {
      throw new Error('Your secure session is unavailable. Please sign in again.')
    }

    await deleteRenterProfile(sessionId)
    history.replace('/application-landing', {cardDeleted: true})
  }

  const handleConfirmFoundPlace = async (outcome: FoundPlaceConfirmationRequest) => {
    if (!sessionId) {
      throw new Error('Your secure session is unavailable. Please sign in again.')
    }

    await confirmFoundPlace({...outcome, sessionId})
    if (outcome.outcomeType === 'submitted_property' || outcome.outcomeType === 'elsewhere') {
      await refreshSubmissions()
    }
  }

  const openFoundPlace = () => {
    setFoundPlaceModalView('options')
    void refreshSubmissions()
  }

  const handleRevokeAndResubmit = async (submission: ScaffoldSubmission) => {
    const wasRevoked = await revokeSubmissionAndUpdate(submission.id)
    if (!wasRevoked) {
      return
    }

    dismissHouseholdCompositionPrompt()
    openSubmitPage(submission)
  }

  if (isLoadingHome) {
    return (
      <div className='renter-home-page'>
        <RenterHomeHero applicantName='Dashboard' submittedCount={0} viewedCount={0} />
        <main className='renter-home-shell'>
          <div className='renter-home-loading'>Loading your Doora Card...</div>
        </main>
      </div>
    )
  }

  if (!sessionId || !summary) {
    return (
      <div className='renter-home-page'>
        <div className='renter-home-shell renter-home-shell-access'>
          <RenterHomeAccessCard
            accessStep={accessStep}
            mobileNumber={mobileNumber}
            otpCode={otpCode}
            accessMessage={accessMessage}
            isSendingCode={isSendingCode}
            isVerifyingCode={isVerifyingCode}
            sendRetrySeconds={Math.max(sendRetry.seconds, verifyRetry.seconds)}
            verifyRetrySeconds={verifyRetry.seconds}
            onOtpCodeChange={setOtpCode}
            onSendCode={handleSendCode}
            onVerifyCode={handleVerifyCode}
            onBack={() => {
              setAccessStep('mobile')
              setAccessMessage(null)
              setOtpCode('')
            }}
            onBuildCard={() => history.push('/application-wizard')}
          />
        </div>
      </div>
    )
  }

  if (!isCompletedCard && summary.hasDashboardAccess !== true) {
    return (
      <div className='renter-home-page'>
        <div className='renter-home-shell renter-home-shell-access'>
          <RenterHomeIncompleteCard
            applicantName={applicantName}
            mobileNumber={summary.mobileNumber || draft?.mobileNumber}
            onContinue={() => goToWizard('review')}
            onBack={() => history.push('/application-landing')}
          />
        </div>
      </div>
    )
  }

  return (
    <div className='renter-home-page'>
      <RenterHomeHero
        applicantName={applicantFirstName}
        submittedCount={submittedCount}
        viewedCount={viewedCount}
        isPrimaryApplicant={isPrimaryApplicant}
        onSubmit={() => openSubmitPage()}
        onFoundPlace={openFoundPlace}
      />

      <main className='renter-home-shell'>
        <RenterHomeTabs activeTab={activeTab} onChange={setActiveTab} />

        {homeMessage && (
          <div className='renter-home-inline-message' role='status'>
            {homeMessage}
          </div>
        )}

        {submissionRefreshFailed && (
          <div className='renter-home-inline-message' role='alert'>
            Your applications could not be refreshed. The list may be out of date.{' '}
            <button
              type='button'
              onClick={() => void refreshSubmissions()}
              disabled={isRefreshingSubmissions}
            >
              {isRefreshingSubmissions ? 'Refreshing...' : 'Refresh applications'}
            </button>
          </div>
        )}
        {(household?.householdEligibility?.warnings ?? []).map((warning) => (
          <div
            key={`${warning.code}-${warning.householdInviteId}`}
            className='renter-home-inline-message'
            role='status'
          >
            {warning.message}
          </div>
        ))}

        {activeTab === 'applications' && (
          <ApplicationsTab
            sessionId={sessionId}
            submissions={submissions}
            onSubmit={() => openSubmitPage()}
            onRestoreAccess={handleRestoreSubmissionAccess}
            onRevoke={handleRevokeSubmission}
            revokingSubmissionId={revokingSubmissionId}
            restoringSubmissionId={restoringSubmissionId}
            applicationType={summary.applicationType}
          />
        )}

        {activeTab === 'household' && (
          <HouseholdTab
            household={household}
            draft={draft}
            isPrimaryApplicant={isPrimaryApplicant}
            sessionId={sessionId}
            onHouseholdChanged={refreshHousehold}
          />
        )}

        {activeTab === 'profile' && (
          <ProfileTab
            summary={summary}
            draft={draft}
            onEditSection={editProfileSection}
            onRetentionPreferenceChange={updateRetentionPreference}
            isSavingRetentionPreference={isSavingRetentionPreference}
            onSettingAction={showScaffoldNotice}
            onDeleteEverything={() => setFoundPlaceModalView('delete-options')}
            onSignOut={() => history.push('/application-landing')}
            isPrimaryApplicant={isPrimaryApplicant}
            primaryApplicant={primaryApplicant}
          />
        )}
      </main>

      {foundPlaceModalView && (
        <FoundPlaceModal
          isOpen={true}
          submissions={submissions}
          initialView={foundPlaceModalView}
          onClose={() => setFoundPlaceModalView(null)}
          onConfirm={handleConfirmFoundPlace}
          onDelete={handleDeleteRenterProfile}
          onRevokeAll={handleRevokeAllSubmissions}
        />
      )}
      {shouldShowHouseholdCompositionPrompt && (
        <HouseholdCompositionPrompt
          submissions={affectedHouseholdSubmissions}
          onDismiss={dismissHouseholdCompositionPrompt}
          onRevokeAndResubmit={handleRevokeAndResubmit}
          revokingSubmissionId={revokingSubmissionId}
        />
      )}
      <ToastContainer
        position='top-right'
        autoClose={1500}
        transition={Bounce}
        pauseOnHover
        pauseOnFocusLoss
        closeOnClick
        draggable
        draggablePercent={80}
        draggableDirection='x'
        role='alert'
        theme='light'
      />
    </div>
  )
}

type HouseholdCompositionPromptProps = {
  submissions: ScaffoldSubmission[]
  onDismiss: () => void
  onRevokeAndResubmit: (submission: ScaffoldSubmission) => void
  revokingSubmissionId?: string | null
}

const getCompositionChangeCopy = (submission: ScaffoldSubmission): string => {
  const added = submission.addedHouseholdMemberNames ?? []
  const removed = submission.removedHouseholdMemberNames ?? []

  if (added.length > 0 && removed.length > 0) {
    return `${added.join(', ')} added; ${removed.join(', ')} removed`
  }

  if (added.length > 0) {
    return `${added.join(', ')} added`
  }

  if (removed.length > 0) {
    return `${removed.join(', ')} removed`
  }

  return 'Household changed'
}

const HouseholdCompositionPrompt: React.FC<HouseholdCompositionPromptProps> = ({
  submissions,
  onDismiss,
  onRevokeAndResubmit,
  revokingSubmissionId,
}) => {
  return (
    <ApplicationResponsiveModal
      ariaLabelledBy='household-composition-modal-title'
      closeAriaLabel='Dismiss household change notice'
      onClose={onDismiss}
      modalClassName='application-household-modal'
      closeButtonClassName='application-household-modal-close'
      mobileAnimationMs={380}
      swipeTarget='sheet'
    >
      <div className='application-household-modal-body household-composition-alert'>
        <div className='head'>
          <h2 id='household-composition-modal-title'>Your household has changed</h2>
          <p>
            Existing submissions keep the household snapshot that was sent. They no longer reflect
            your current household unless you revoke access and send an updated application.
          </p>
        </div>

        <div className='affected-submission-list'>
          {submissions.map((submission) => (
            <div className='affected-submission' key={submission.id}>
              <div>
                <strong>{submission.address || 'Submitted property'}</strong>
                <span>
                  {getCompositionChangeCopy(submission)} - Submitted{' '}
                  {formatDate(submission.submittedAt)}
                </span>
              </div>
              <button
                type='button'
                disabled={Boolean(revokingSubmissionId)}
                onClick={() => onRevokeAndResubmit(submission)}
              >
                {revokingSubmissionId === submission.id ? 'Revoking...' : 'Revoke and resubmit'}
              </button>
            </div>
          ))}
        </div>

        <button type='button' className='keep-submissions' onClick={onDismiss}>
          Keep existing submissions
        </button>
      </div>
    </ApplicationResponsiveModal>
  )
}

type RenterHomeAccessCardProps = {
  accessStep: AccessStep
  mobileNumber: string
  otpCode: string
  accessMessage: string | null
  isSendingCode: boolean
  isVerifyingCode: boolean
  sendRetrySeconds: number
  verifyRetrySeconds: number
  onOtpCodeChange: (value: string) => void
  onSendCode: (mobileNumber: string) => void | Promise<void>
  onVerifyCode: (event: FormEvent) => void
  onBack: () => void
  onBuildCard: () => void
}

type RenterHomeAccessForm = {
  mobileNumber: string
}

const RenterHomeAccessCard: React.FC<RenterHomeAccessCardProps> = ({
  accessStep,
  mobileNumber,
  otpCode,
  accessMessage,
  isSendingCode,
  isVerifyingCode,
  sendRetrySeconds,
  verifyRetrySeconds,
  onOtpCodeChange,
  onSendCode,
  onVerifyCode,
  onBack,
  onBuildCard,
}) => {
  const {
    control,
    handleSubmit,
    formState: {errors},
  } = useForm<RenterHomeAccessForm>({
    defaultValues: {
      mobileNumber,
    },
    mode: 'onChange',
  })

  return (
    <div className='renter-access-card'>
      <div className='text-center mb-6'>
        <img src='/media/logos/doora-logo.png' alt='Doora' className='renter-access-logo' />
        <h1>Open your Doora Card</h1>
        <p>Verify your mobile number so we can show the right renter dashboard.</p>
      </div>

      {accessStep === 'mobile' ? (
        <form onSubmit={handleSubmit((values) => onSendCode(values.mobileNumber))}>
          <MobileNumberInput<RenterHomeAccessForm>
            name='mobileNumber'
            label='Mobile Number'
            placeholder='0412 345 678'
            control={control}
            errors={errors}
            className='mb-0'
            inputClassName='form-control-lg renter-access-input'
            disabled={isSendingCode || sendRetrySeconds > 0}
            rules={{
              required: 'Enter a valid Australian mobile number.',
              validate: (value) =>
                AU_MOBILE_REGEX.test(normalizeMobile(String(value))) ||
                'Enter a valid Australian mobile number.',
            }}
          />
          <button
            type='submit'
            className='btn btn-doora w-100 rounded-pill mt-4'
            disabled={isSendingCode || sendRetrySeconds > 0}
          >
            {isSendingCode
              ? 'Sending code...'
              : sendRetrySeconds > 0
              ? `Try again in ${sendRetrySeconds}s`
              : 'Send Code'}
          </button>
        </form>
      ) : (
        <form onSubmit={onVerifyCode}>
          <div className='renter-access-mobile'>
            Code sent to {formatAustralianMobile(mobileNumber)}
            <button type='button' className='btn btn-link p-0 ms-2' onClick={onBack}>
              Change
            </button>
          </div>
          <label className='form-label fw-semibold' htmlFor='renter-home-otp'>
            6-digit code
          </label>
          <OTPInput<{otpCode: string}>
            name='otpCode'
            length={6}
            numericOnly={true}
            disabled={isVerifyingCode || verifyRetrySeconds > 0}
            className='renter-otp-boxes justify-content-center my-4'
            onChange={(value) => onOtpCodeChange(value.replace(/\D/g, '').slice(0, 6))}
          />
          <button
            type='submit'
            className='btn btn-doora w-100 rounded-pill mt-4'
            disabled={isVerifyingCode || verifyRetrySeconds > 0}
          >
            {isVerifyingCode
              ? 'Verifying...'
              : verifyRetrySeconds > 0
              ? `Try again in ${verifyRetrySeconds}s`
              : 'Open My Doora Card'}
          </button>
        </form>
      )}

      {accessMessage && (
        <div className='renter-access-message' role='status'>
          {accessMessage}
        </div>
      )}

      <button type='button' className='btn btn-link renter-access-secondary' onClick={onBuildCard}>
        Build a new Doora Card
      </button>
    </div>
  )
}

type RenterHomeIncompleteCardProps = {
  applicantName: string
  mobileNumber?: string
  onContinue: () => void
  onBack: () => void
}

const RenterHomeIncompleteCard: React.FC<RenterHomeIncompleteCardProps> = ({
  applicantName,
  mobileNumber,
  onContinue,
  onBack,
}) => (
  <div className='renter-access-card'>
    <div className='text-center mb-6'>
      <img src='/media/logos/doora-logo.png' alt='Doora' className='renter-access-logo' />
      <h1>No completed Doora Card yet</h1>
      <p>
        {applicantName && applicantName !== 'Renter' ? `${applicantName}, ` : ''}
        this mobile {mobileNumber ? `(${formatAustralianMobile(mobileNumber)}) ` : ''}has a draft,
        but it has not passed the final review checklist yet.
      </p>
    </div>

    <button type='button' className='btn btn-doora w-100 rounded-pill' onClick={onContinue}>
      Continue Building
    </button>
    <button type='button' className='btn btn-link renter-access-secondary' onClick={onBack}>
      Back to landing
    </button>
  </div>
)

type RenterHomeTabsProps = {
  activeTab: RenterHomeTab
  onChange: (tab: RenterHomeTab) => void
}

const RenterHomeTabs: React.FC<RenterHomeTabsProps> = ({activeTab, onChange}) => {
  const tabs: Array<{key: RenterHomeTab; label: string}> = [
        {key: 'applications', label: 'Submissions'},
        {key: 'household', label: 'Household'},
        {key: 'profile', label: 'Your Doora Card'},
      ]

  return (
    <>
      <div className='renter-home-tabs' role='tablist' aria-label='Renter dashboard sections'>
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type='button'
            role='tab'
            aria-selected={activeTab === tab.key}
            className={`renter-home-tab ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => onChange(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>
    </>
  )
}

export default RenterHomePage
